/**
 * Phase 6.1: FlexRelationshipService
 *
 * This service enables intelligent handling of flex container parent-child relationships.
 * When users select components inside a flex container and issue commands like "align left",
 * the system automatically modifies the parent container's properties instead of requiring
 * manual intervention.
 *
 * Key Features:
 * - Detects if components are flex children
 * - Translates child-targeted commands to parent property changes
 * - Supports bidirectional modifications (both parent and children)
 * - Provides flex-aware suggestions
 *
 * @see Documents/AI_Template_System/Roadmaps/AI_Chat_Enhancement_Roadmap_Phase2.md
 */

import { Component } from '@/types';
import { CSSProperties } from 'react';
import { logger } from '@/utils/logger';

/**
 * Represents a property change to be applied
 */
export interface PropertyChange {
  style?: CSSProperties;
  properties?: Record<string, unknown>;
  size?: { width?: number | string; height?: number | string };
  responsivePosition?: Record<string, unknown>;
}

/**
 * Represents the flex relationship context for a component
 */
export interface FlexRelationship {
  /** Whether this component is a child of a flex container */
  isFlexChild: boolean;
  /** The ID of the parent component (if any) */
  parentId: string | null;
  /** The parent component object (if available) */
  parentComponent: Component | null;
  /** The flex-direction of the parent container */
  flexDirection: 'row' | 'column' | null;
  /** Current justify-content value of the parent */
  justifyContent: string | null;
  /** Current align-items value of the parent */
  alignItems: string | null;
  /** Current gap value of the parent */
  gap: string | number | null;
  /** Number of sibling components (including this one) */
  siblingCount: number;
  /** Index of this component among its siblings */
  childIndex: number;
  /** IDs of all sibling components */
  siblingIds: string[];
}

/**
 * Represents a translated command for flex children
 */
export interface FlexCommandTranslation {
  /** Whether changes target parent, children, or both */
  targetType: 'parent' | 'children' | 'both';
  /** Changes to apply to the parent container */
  parentChanges?: PropertyChange;
  /** Changes to apply to each child (indexed by position or by component ID) */
  childChanges?: PropertyChange[];
  /** Human-readable description of what was changed */
  description: string;
}

/**
 * Command pattern translator function type
 */
type CommandTranslator = (
  relationship: FlexRelationship,
  match: RegExpMatchArray
) => FlexCommandTranslation;

/**
 * Helper type for the command map entries
 */
type CommandMapEntry = [RegExp, CommandTranslator];

/**
 * Map of flex command patterns to their translation functions
 */
const FLEX_COMMAND_MAP_ENTRIES: CommandMapEntry[] = [
  // ============================================================================
  // ALIGNMENT COMMANDS -> parent justify-content
  // ============================================================================

  // "align left", "align to the left", "left align"
  [
    /(?:align\s*(?:to\s*(?:the\s*)?)?(?:left|start))|(?:left\s*align)/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { justifyContent: 'flex-start' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with justify-content: flex-start`,
    }),
  ],

  // "align right", "align to the right", "right align"
  [
    /(?:align\s*(?:to\s*(?:the\s*)?)?(?:right|end))|(?:right\s*align)/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { justifyContent: 'flex-end' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with justify-content: flex-end`,
    }),
  ],

  // "align center", "center align", "center them" - but NOT "center vertically"
  [
    /(?:align\s*(?:to\s*(?:the\s*)?)?center)|(?:center\s*(?:align|them))$|^center$/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { justifyContent: 'center' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with justify-content: center`,
    }),
  ],

  // ============================================================================
  // DISTRIBUTION COMMANDS -> parent justify-content
  // ============================================================================

  // "distribute evenly", "space evenly", "equal spacing"
  [
    /(?:distribute|space)\s*(?:evenly|equally)|equal\s*(?:spacing|gaps?)/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { justifyContent: 'space-evenly' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with justify-content: space-evenly`,
    }),
  ],

  // "space between", "space apart", "spread out"
  [
    /(?:space\s*(?:between|apart))|(?:spread\s*(?:out|them)?)/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { justifyContent: 'space-between' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with justify-content: space-between`,
    }),
  ],

  // "space around"
  [
    /space\s*around/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { justifyContent: 'space-around' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with justify-content: space-around`,
    }),
  ],

  // ============================================================================
  // GAP COMMANDS -> parent gap
  // ============================================================================

  // "stack with 16px gap", "space with 20px spacing", "add 12px gap"
  [
    /(?:stack|space|add)\s*(?:with|using)?\s*(\d+)\s*(?:px)?\s*(?:gap|spacing)?/i,
    (ctx, match) => ({
      targetType: 'parent',
      parentChanges: { style: { gap: `${match[1]}px` } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with gap: ${match[1]}px`,
    }),
  ],

  // "16px gap", "20 spacing", "gap 16"
  [
    /^(\d+)\s*(?:px)?\s*(?:gap|spacing)$/i,
    (ctx, match) => ({
      targetType: 'parent',
      parentChanges: { style: { gap: `${match[1]}px` } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with gap: ${match[1]}px`,
    }),
  ],

  // "gap 16", "set gap to 20"
  [
    /(?:set\s*)?gap\s*(?:to\s*)?(\d+)\s*(?:px)?/i,
    (ctx, match) => ({
      targetType: 'parent',
      parentChanges: { style: { gap: `${match[1]}px` } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with gap: ${match[1]}px`,
    }),
  ],

  // "more gap", "increase gap", "bigger gap"
  [
    /(?:more|increase|bigger)\s*gap/i,
    (ctx) => {
      const currentGap = parseGap(ctx.gap);
      const newGap = currentGap + 8;
      return {
        targetType: 'parent',
        parentChanges: { style: { gap: `${newGap}px` } },
        description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with gap: ${newGap}px`,
      };
    },
  ],

  // "less gap", "decrease gap", "smaller gap"
  [
    /(?:less|decrease|smaller)\s*gap/i,
    (ctx) => {
      const currentGap = parseGap(ctx.gap);
      const newGap = Math.max(0, currentGap - 8);
      return {
        targetType: 'parent',
        parentChanges: { style: { gap: `${newGap}px` } },
        description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with gap: ${newGap}px`,
      };
    },
  ],

  // "no gap", "remove gap"
  [
    /(?:no|remove)\s*gap/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { gap: '0px' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with gap: 0`,
    }),
  ],

  // ============================================================================
  // BIDIRECTIONAL COMMANDS -> both parent and children
  // NOTE: These must come BEFORE simpler patterns to avoid partial matches
  // ============================================================================

  // "fill the width evenly with 16px gaps", "fill width evenly with 20 gaps"
  [
    /fill\s*(?:the\s*)?width\s*evenly\s*(?:with)?\s*(\d+)\s*(?:px)?\s*gaps?/i,
    (ctx, match) => ({
      targetType: 'both',
      parentChanges: {
        style: {
          justifyContent: 'space-between',
          gap: `${match[1]}px`,
        },
      },
      childChanges: Array(ctx.siblingCount).fill({ style: { flex: '1' } }),
      description: `✅ Updated parent with justify-content: space-between, gap: ${match[1]}px, and set flex: 1 on ${ctx.siblingCount} children`,
    }),
  ],

  // ============================================================================
  // EQUAL WIDTH/HEIGHT COMMANDS -> children flex
  // ============================================================================

  // "equal width", "same width", "fill width" - but NOT "fill width evenly"
  [
    /(?:equal|same)\s*width|fill\s*width(?!\s*evenly)/i,
    (ctx) => ({
      targetType: 'children',
      childChanges: Array(ctx.siblingCount).fill({ style: { flex: '1' } }),
      description: `✅ Set flex: 1 on ${ctx.siblingCount} children for equal width`,
    }),
  ],

  // "equal height", "same height"
  [
    /(?:equal|same)\s*height/i,
    (ctx) => ({
      targetType: 'children',
      childChanges: Array(ctx.siblingCount).fill({ style: { flex: '1' } }),
      description: `✅ Set flex: 1 on ${ctx.siblingCount} children for equal height`,
    }),
  ],

  // ============================================================================
  // VERTICAL ALIGNMENT COMMANDS -> parent align-items
  // ============================================================================

  // "vertically center", "center vertically", "vertical center"
  [
    /(?:vertically?\s*center)|(?:center\s*vertically)/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { alignItems: 'center' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with align-items: center`,
    }),
  ],

  // "align top vertically", "top align", "top vertically"
  [
    /(?:(?:align\s*)?(?:top|start)\s*vertically)|(?:top\s*align)/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { alignItems: 'flex-start' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with align-items: flex-start`,
    }),
  ],

  // "align bottom vertically", "bottom align", "bottom vertically"
  [
    /(?:(?:align\s*)?(?:bottom|end)\s*vertically)|(?:bottom\s*align)/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { alignItems: 'flex-end' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with align-items: flex-end`,
    }),
  ],

  // "stretch", "stretch vertically"
  [
    /stretch(?:\s*vertically)?/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { style: { alignItems: 'stretch' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with align-items: stretch`,
    }),
  ],

  // "make first item twice as wide", "first card 2x wide"
  [
    /(?:make\s*)?first\s*(?:item|card|component)?\s*(?:twice|2x)\s*(?:as\s*)?wide/i,
    (ctx) => ({
      targetType: 'children',
      childChanges:
        ctx.siblingCount > 0
          ? [
              { style: { flex: '2' } },
              ...Array(ctx.siblingCount - 1).fill({ style: { flex: '1' } }),
            ]
          : undefined,
      description: `✅ Set flex: 2 on first child, flex: 1 on remaining ${ctx.siblingCount - 1} children`,
    }),
  ],

  // "wrap", "wrap items", "flex wrap"
  [
    /(?:flex\s*)?wrap(?:\s*items)?/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { properties: { flexWrap: 'wrap' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with flex-wrap: wrap`,
    }),
  ],

  // "nowrap", "no wrap", "don't wrap"
  [
    /(?:no\s*wrap|nowrap|don'?t\s*wrap)/i,
    (ctx) => ({
      targetType: 'parent',
      parentChanges: { properties: { flexWrap: 'nowrap' } },
      description: `✅ Updated parent container "${ctx.parentComponent?.name || ctx.parentId}" with flex-wrap: nowrap`,
    }),
  ],
];

/**
 * Map of flex command patterns created from the entries array
 */
const FLEX_COMMAND_MAP: Map<RegExp, CommandTranslator> = new Map(FLEX_COMMAND_MAP_ENTRIES);

/**
 * Helper function to parse gap value to number
 */
function parseGap(gap: string | number | null): number {
  if (gap === null || gap === undefined) return 0;
  if (typeof gap === 'number') return gap;
  const parsed = parseInt(gap, 10);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * FlexRelationshipService - Singleton service for flex parent-child relationship handling
 */
class FlexRelationshipService {
  private static instance: FlexRelationshipService | null = null;

  private constructor() {
    logger.debug("FlexRelationshipService initialized");
  }

  /**
   * Get the singleton instance of FlexRelationshipService
   */
  static getInstance(): FlexRelationshipService {
    if (!this.instance) {
      this.instance = new FlexRelationshipService();
    }
    return this.instance;
  }

  /**
   * Analyzes whether a component is a flex child and gathers context about the relationship.
   *
   * @param component The component to analyze
   * @param allComponents Array of all components for parent lookup
   * @returns FlexRelationship object with full context
   */
  analyzeFlexRelationship(
    component: Component,
    allComponents: Component[]
  ): FlexRelationship {
    // Find parent component
    const parent = this.findParentComponent(component, allComponents);

    if (!parent) {
      return this.createNonFlexRelationship();
    }

    // Check if parent is a flex container
    const parentStyle = this.extractStyle(parent);
    const isFlexContainer =
      parentStyle.display === 'flex' ||
      parentStyle.display === 'inline-flex' ||
      parent.type === 'flex-container';

    if (!isFlexContainer) {
      return {
        isFlexChild: false,
        parentId: parent.id,
        parentComponent: parent,
        flexDirection: null,
        justifyContent: null,
        alignItems: null,
        gap: null,
        siblingCount: parent.children?.length || 0,
        childIndex: parent.children?.indexOf(component.id) ?? -1,
        siblingIds: parent.children || [],
      };
    }

    const relationship: FlexRelationship = {
      isFlexChild: true,
      parentId: parent.id,
      parentComponent: parent,
      flexDirection: (parentStyle.flexDirection as 'row' | 'column') || 'row',
      justifyContent: (parentStyle.justifyContent as string) || null,
      alignItems: (parentStyle.alignItems as string) || null,
      gap: parentStyle.gap || null,
      siblingCount: parent.children?.length || 0,
      childIndex: parent.children?.indexOf(component.id) ?? -1,
      siblingIds: parent.children || [],
    };

    return relationship;
  }

  /**
   * Analyzes multiple components and returns their common flex relationship if they share a parent.
   *
   * @param components Array of components to analyze
   * @param allComponents Array of all components for parent lookup
   * @returns FlexRelationship if all components share the same flex parent, null otherwise
   */
  analyzeMultipleFlexRelationships(
    components: Component[],
    allComponents: Component[]
  ): FlexRelationship | null {
    if (components.length === 0) {
      return null;
    }

    // Analyze each component
    const relationships = components.map((c) =>
      this.analyzeFlexRelationship(c, allComponents)
    );

    // Check if all are flex children
    const allFlexChildren = relationships.every((r) => r.isFlexChild);
    if (!allFlexChildren) {
      console.log('[Phase 6.1] Not all components are flex children');
      return null;
    }

    // Check if all share the same parent
    const parentIds = new Set(relationships.map((r) => r.parentId));
    if (parentIds.size !== 1) {
      console.log('[Phase 6.1] Components have different flex parents:', parentIds);
      return null;
    }

    // Return the first relationship (they're all equivalent for the common parent)
    return relationships[0];
  }

  /**
   * Finds the parent component for a given component.
   */
  private findParentComponent(
    component: Component,
    allComponents: Component[]
  ): Component | null {
    // First try parentId
    if (component.parentId) {
      const parent = allComponents.find((c) => c.id === component.parentId);
      if (parent) return parent;
    }

    // Then try finding by children array
    return (
      allComponents.find(
        (c) => c.children?.includes(component.id)
      ) || null
    );
  }

  /**
   * Creates a non-flex relationship result.
   */
  private createNonFlexRelationship(): FlexRelationship {
    return {
      isFlexChild: false,
      parentId: null,
      parentComponent: null,
      flexDirection: null,
      justifyContent: null,
      alignItems: null,
      gap: null,
      siblingCount: 0,
      childIndex: -1,
      siblingIds: [],
    };
  }

  /**
   * Extracts combined style from all possible locations in a component.
   */
  private extractStyle(component: Component): CSSProperties {
    const breakpointStyle =
      component.responsivePosition?.breakpoints?.desktop?.style || {};
    const componentStyle = (component.style as CSSProperties) || {};
    const propertyStyle = (component.properties?.style as CSSProperties) || {};

    // Merge with priority: breakpoint > component style > property style
    return { ...propertyStyle, ...componentStyle, ...breakpointStyle };
  }

  /**
   * Checks if a command should be translated for flex children.
   *
   * @param command The user's command string
   * @returns true if this command can be translated for flex children
   */
  isFlexChildCommand(command: string): boolean {
    const normalizedCommand = command.trim().toLowerCase();

    for (const pattern of FLEX_COMMAND_MAP.keys()) {
      if (pattern.test(normalizedCommand)) {
        console.log('[Phase 6.1] Flex child command detected:', command);
        return true;
      }
    }

    return false;
  }

  /**
   * Translates a command for flex children to parent/child modifications.
   *
   * @param command The user's command string
   * @param relationship The flex relationship context
   * @returns FlexCommandTranslation or null if no translation applies
   */
  translateCommand(
    command: string,
    relationship: FlexRelationship
  ): FlexCommandTranslation | null {
    if (!relationship.isFlexChild) {
      return null;
    }

    const normalizedCommand = command.trim().toLowerCase();

    for (const [pattern, translator] of FLEX_COMMAND_MAP.entries()) {
      const match = normalizedCommand.match(pattern);
      if (match) {
        const translation = translator(relationship, match);

        // Phase 6.3: Enhanced logging for bidirectional commands
        if (translation.targetType === 'both') {
          console.log('[Phase 6.3] Bidirectional command detected:', normalizedCommand);
          console.log('[Phase 6.3] Applying to parent and', translation.childChanges?.length || 0, 'children');
          console.log('[Phase 6.3] Parent changes:', translation.parentChanges);
          console.log('[Phase 6.3] Child changes:', translation.childChanges);
        } else {
          console.log('[Phase 6.1] Command translation:', {
            command: normalizedCommand,
            targetType: translation.targetType,
            parentChanges: translation.parentChanges,
            childChangesCount: translation.childChanges?.length,
          });
        }

        return translation;
      }
    }

    return null;
  }

  /**
   * Gets all registered flex command patterns for documentation/testing.
   */
  getCommandPatterns(): RegExp[] {
    return Array.from(FLEX_COMMAND_MAP.keys());
  }

  /**
   * Gets context-aware suggestions for flex children.
   *
   * @param relationship The flex relationship context
   * @returns Array of suggested commands
   */
  getFlexSuggestions(relationship: FlexRelationship): string[] {
    if (!relationship.isFlexChild) {
      return [];
    }

    const suggestions: string[] = [];

    // Alignment suggestions
    if (relationship.justifyContent !== 'flex-start') {
      suggestions.push('align left');
    }
    if (relationship.justifyContent !== 'center') {
      suggestions.push('align center');
    }
    if (relationship.justifyContent !== 'flex-end') {
      suggestions.push('align right');
    }

    // Distribution suggestions
    if (relationship.justifyContent !== 'space-evenly') {
      suggestions.push('distribute evenly');
    }
    if (relationship.justifyContent !== 'space-between') {
      suggestions.push('space between');
    }

    // Gap suggestions
    const currentGap = parseGap(relationship.gap);
    if (currentGap === 0) {
      suggestions.push('add 16px gap');
    } else {
      suggestions.push('more gap');
      suggestions.push('less gap');
    }

    // Equal width suggestion
    suggestions.push('equal width');

    // Vertical alignment suggestions
    if (relationship.alignItems !== 'center') {
      suggestions.push('center vertically');
    }

    return suggestions.slice(0, 8); // Return top 8 suggestions
  }

  /**
   * Creates a user-friendly message describing the flex context.
   *
   * @param relationship The flex relationship context
   * @returns Human-readable description of the flex context
   */
  getFlexContextDescription(relationship: FlexRelationship): string {
    if (!relationship.isFlexChild) {
      return '';
    }

    const parentName = relationship.parentComponent?.name || relationship.parentId;
    const direction = relationship.flexDirection === 'column' ? 'vertical' : 'horizontal';

    return `Flex child of "${parentName}" (${direction} layout, ${relationship.siblingCount} items)`;
  }
}

// Export singleton instance
export const flexRelationshipService = FlexRelationshipService.getInstance();

// Also export the class for testing
export { FlexRelationshipService };
